import React, { useState, useEffect, useRef, useCallback } from "react";
import { useEditor } from "@grapesjs/react";
import TemplateLibrary from "./TemplateLibrary";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";

// SVG icons styled at 14×14, stroke-based
const I = {
  desktop: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
    </svg>
  ),
  tablet: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="4" y="2" width="16" height="20" rx="2"/><path d="M12 18h.01"/>
    </svg>
  ),
  mobile: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="7" y="2" width="10" height="20" rx="2"/><path d="M12 18h.01"/>
    </svg>
  ),
  zoomOut: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5M8 11h6"/>
    </svg>
  ),
  zoomIn: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5M11 8v6M8 11h6"/>
    </svg>
  ),
  list: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>
    </svg>
  ),
  trash: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
    </svg>
  ),
  eye: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12Z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ),
  undo: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-15-6.7L3 13"/>
    </svg>
  ),
  redo: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M21 7v6h-6"/><path d="M3 17a9 9 0 0 1 15-6.7L21 13"/>
    </svg>
  ),
  save: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z"/>
      <path d="M17 21v-8H7v8M7 3v5h8"/>
    </svg>
  ),
  sparkle: (p: any) => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" {...p}>
      <path d="m12 2 2 6 6 2-6 2-2 6-2-6-6-2 6-2Z"/>
    </svg>
  ),
  code: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="m16 18 6-6-6-6M8 6l-6 6 6 6"/>
    </svg>
  ),
  grid: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="3" y="3" width="18" height="18" rx="1"/>
      <path d="M3 9h18M3 15h18M9 3v18M15 3v18"/>
    </svg>
  ),
  fullscreen: (p: any) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3"/>
    </svg>
  ),
  chevDown: (p: any) => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="m6 9 6 6 6-6"/>
    </svg>
  ),
};

interface CanvasToolbarProps {
  saveStatus?: "saving" | "saved" | "error";
  lastSaved?: Date | null;
}

export default function CanvasToolbar({ saveStatus = "saved", lastSaved }: CanvasToolbarProps): React.ReactElement {
  const editor = useEditor();
  const { Commands } = editor;
  const [device, setDeviceState] = useState("Desktop");
  const [zoom, setZoom] = useState(100);
  const [gridOn, setGridOn] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [pagesOpen, setPagesOpen] = useState(false);
  const [pages, setPages] = useState<any[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<string>("");
  const pagesDropRef = useRef<HTMLDivElement>(null);
  const { user, fetchUserProfile } = useAuth() as any;
  const activeCrmDeal = user?.activeCrmDeal;

  const handleUnlinkDeal = useCallback(async () => {
    try {
      await apiService.unlinkActiveDeal();
      await fetchUserProfile?.();
    } catch (err) {
      console.warn("⚠️ Failed to unlink active deal:", (err as Error).message);
    }
  }, [fetchUserProfile]);

  const refreshPages = useCallback(() => {
    const all = (editor as any).Pages?.getAll() ?? [];
    const sel = (editor as any).Pages?.getSelected();
    setPages([...all]);
    setSelectedPageId(sel?.getId() ?? "");
  }, [editor]);

  useEffect(() => {
    const updateZoom = () => setZoom(Math.round(editor.Canvas.getZoom()));
    editor.on("canvas:zoom", updateZoom);
    editor.on("page:add page:remove page:select", refreshPages);
    refreshPages();
    return () => {
      editor.off("canvas:zoom", updateZoom);
      editor.off("page:add page:remove page:select", refreshPages);
    };
  }, [editor, refreshPages]);

  // Close pages dropdown on outside click
  useEffect(() => {
    if (!pagesOpen) return;
    const onOutside = (e: MouseEvent) => {
      if (pagesDropRef.current && !pagesDropRef.current.contains(e.target as Node)) {
        setPagesOpen(false);
      }
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [pagesOpen]);

  const handleDevice = (d: string) => {
    const map: Record<string, string> = {
      Desktop: "set-device-desktop",
      Tablet: "set-device-tablet",
      Mobile: "set-device-mobile",
    };
    Commands.run(map[d]);
    setDeviceState(d);
  };

  const handleZoomIn = () => editor.Canvas.setZoom(Math.min(200, editor.Canvas.getZoom() + 10));
  const handleZoomOut = () => editor.Canvas.setZoom(Math.max(20, editor.Canvas.getZoom() - 10));
  const handleZoomReset = () => editor.Canvas.setZoom(100);

  const handlePageManager = () => {
    refreshPages();
    setPagesOpen((v) => !v);
  };

  const handleSelectPage = (pageId: string) => {
    const page = (editor as any).Pages?.get(pageId);
    if (page) {
      (editor as any).Pages.select(page);
      setSelectedPageId(pageId);
    }
    setPagesOpen(false);
  };

  // Pages default to "Page 2"/"Page 3"/etc with no way to rename them --
  // the publish pipeline (MainNav's handlePublish) derives each page's real
  // URL by slugifying this exact name, so a never-renamed page always
  // publishes as /page-2 instead of whatever the nav links actually point
  // to. See issue #36.
  const [renamingPageId, setRenamingPageId] = useState<string>("");
  const [renameValue, setRenameValue] = useState<string>("");

  const startRenamePage = (id: string, currentName: string) => {
    setRenamingPageId(id);
    setRenameValue(currentName);
  };

  const commitRenamePage = () => {
    const name = renameValue.trim();
    const page = renamingPageId ? (editor as any).Pages?.get(renamingPageId) : null;
    if (page && name) {
      page.setName(name);
      refreshPages();
    }
    setRenamingPageId("");
  };

  const handleAddPage = () => {
    const all = (editor as any).Pages?.getAll() ?? [];
    const timestamp = Math.floor(Date.now() / 1000).toString(16).padStart(8, "0");
    const rand = Array.from({ length: 16 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
    const newPage = (editor as any).Pages?.add({
      id: timestamp + rand,
      name: `Page ${all.length + 1}`,
    });
    if (newPage) {
      (editor as any).Pages.select(newPage);
    }
    refreshPages();
  };

  const handleToggleGrid = () => {
    if (Commands.has("toggle-grid")) {
      Commands.run("toggle-grid");
      setGridOn((v) => !v);
    }
  };

  const handleToggleFullscreen = () => {
    if (Commands.isActive("fullscreen")) {
      Commands.stop("fullscreen");
      setFullscreen(false);
    } else {
      Commands.run("fullscreen");
      setFullscreen(true);
    }
  };

  const saveLabel = () => {
    if (saveStatus === "saving") return "Saving…";
    if (saveStatus === "error") return "Error";
    if (!lastSaved) return "Saved";
    const mins = Math.round((Date.now() - lastSaved.getTime()) / 60000);
    return mins < 1 ? "Just saved" : `${mins}m ago`;
  };

  return (
    <>
      <div className="dx-canvas-toolbar">
        {/* Device */}
        <div className="dx-tb-group">
          <button className={`dx-tb-btn${device === "Desktop" ? " active" : ""}`} onClick={() => handleDevice("Desktop")} title="Desktop">
            <I.desktop />
          </button>
          <button className={`dx-tb-btn${device === "Tablet" ? " active" : ""}`} onClick={() => handleDevice("Tablet")} title="Tablet">
            <I.tablet />
          </button>
          <button className={`dx-tb-btn${device === "Mobile" ? " active" : ""}`} onClick={() => handleDevice("Mobile")} title="Mobile">
            <I.mobile />
          </button>
        </div>

        {/* Zoom */}
        <div className="dx-zoom-group">
          <button className="dx-tb-btn" onClick={handleZoomOut} title="Zoom out"><I.zoomOut /></button>
          <span className="dx-zoom-val" onClick={handleZoomReset} title="Reset zoom">{zoom}%</span>
          <button className="dx-tb-btn" onClick={handleZoomIn} title="Zoom in"><I.zoomIn /></button>
        </div>

        <div className="dx-divider-v" />

        {/* Pages / Delete / Preview */}
        <div className="dx-tb-group" style={{ position: "relative" }}>
          <div ref={pagesDropRef} style={{ position: "relative" }}>
            <button
              className={`dx-tb-btn${pagesOpen ? " active" : ""}`}
              onClick={handlePageManager}
              title="Switch page"
            >
              <I.list />
            </button>
            {pagesOpen && (
              <div className="dx-pages-drop">
                <div className="dx-pages-drop-header">
                  <span>Pages</span>
                  <button className="dx-pages-add-btn" onClick={handleAddPage} title="Add page">+ Add</button>
                </div>
                {pages.length === 0 ? (
                  <div className="dx-pages-empty">No pages</div>
                ) : (
                  pages.map((p: any) => {
                    const id = p.getId?.() ?? p.id;
                    const name = p.getName?.() ?? p.name ?? "Untitled";
                    const active = id === selectedPageId;
                    const isRenaming = renamingPageId === id;
                    return (
                      <div
                        key={id}
                        className={`dx-pages-item${active ? " active" : ""}`}
                        onClick={() => !isRenaming && handleSelectPage(id)}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/>
                          <path d="M14 2v6h6"/>
                        </svg>
                        {isRenaming ? (
                          <input
                            autoFocus
                            className="dx-pages-rename-input"
                            value={renameValue}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={commitRenamePage}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitRenamePage();
                              if (e.key === "Escape") setRenamingPageId("");
                            }}
                          />
                        ) : (
                          <span>{name}</span>
                        )}
                        {active && !isRenaming && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: isRenaming ? 0 : "auto", flexShrink: 0 }}>
                            <path d="M20 6 9 17l-5-5"/>
                          </svg>
                        )}
                        {!isRenaming && (
                          <button
                            className="dx-pages-rename-btn"
                            style={{ marginLeft: active ? 6 : "auto", flexShrink: 0, background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", color: "inherit", opacity: 0.6 }}
                            title="Rename page"
                            onClick={(e) => { e.stopPropagation(); startRenamePage(id, name); }}
                          >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
          <button className="dx-tb-btn danger" onClick={() => Commands.run("clear-canvas")} title="Clear canvas"><I.trash /></button>
          <button className="dx-tb-btn" onClick={() => Commands.run("preview-template")} title="Preview"><I.eye /></button>
        </div>

        {/* Undo / Redo / Save */}
        <div className="dx-tb-group">
          <button className="dx-tb-btn" onClick={() => Commands.run("core:undo")} title="Undo"><I.undo /></button>
          <button className="dx-tb-btn" onClick={() => Commands.run("core:redo")} title="Redo"><I.redo /></button>
          <button className="dx-tb-btn" onClick={() => (window as any).saveCurrentPage?.()} title="Save"><I.save /></button>
        </div>

        <div className="dx-divider-v" />

        {/* Templates / AI / Publish */}
        <button className="dx-tb-pill" onClick={() => setTemplatesOpen(true)}>
          Templates <I.chevDown />
        </button>
        <button
          className="dx-tb-pill ai"
          onClick={() => { const fn = (window as any).showGptPanel; if (fn) fn(); }}
        >
          <I.sparkle /> AI
        </button>
        <button className="dx-tb-pill publish" onClick={() => (window as any).handlePublish?.()}>
          Publish
        </button>

        {activeCrmDeal && (
          <div className="dx-tb-pill dx-deal-pill" title={`Linked to deal: ${activeCrmDeal.address || ""}`}>
            <span>Linked to deal: {activeCrmDeal.address}</span>
            <button
              type="button"
              className="dx-deal-pill-close"
              onClick={handleUnlinkDeal}
              aria-label="Unlink deal"
            >
              ×
            </button>
          </div>
        )}

        {/* Save status */}
        <div className="dx-save-status" title={lastSaved ? `Last saved: ${lastSaved.toLocaleTimeString()}` : ""}>
          <span className={`dx-dot ${saveStatus === "saving" ? "saving" : saveStatus === "error" ? "error" : "ok"}`} />
          <span className="dx-save-label">{saveLabel()}</span>
        </div>

        <div className="dx-divider-v" />

        {/* Code / Grid / Fullscreen */}
        <div className="dx-tb-group">
          <button className="dx-tb-btn" onClick={() => Commands.run("export-code")} title="View code"><I.code /></button>
          <button className={`dx-tb-btn${gridOn ? " active" : ""}`} onClick={handleToggleGrid} title="Toggle grid"><I.grid /></button>
          <button className={`dx-tb-btn${fullscreen ? " active" : ""}`} onClick={handleToggleFullscreen} title="Fullscreen"><I.fullscreen /></button>
        </div>
      </div>

      <TemplateLibrary isOpen={templatesOpen} onClose={() => setTemplatesOpen(false)} />
    </>
  );
}
